import { useEffect, useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import "./ebay.css";
import { signOutRequest } from "../../redux/auth/auth.actions";
import { toastify } from "../../configs/utils";
import { startEbaySync } from "../../services/ebaySync";
import { EBAY_SYNC_DETAIL } from "../../routes/routes";

function Ebay() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [cards, setCards] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [nextPage, setNextPage] = useState(null);
  const [prevPage, setPrevPage] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showSelectedOnly, setShowSelectedOnly] = useState(false);
  const [selectedById, setSelectedById] = useState(new Map());
  const [ordering, setOrdering] = useState("-uploaded_at");
  const [search, setSearch] = useState("");
  const [filterId, setFilterId] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterBatch, setFilterBatch] = useState("");
  const [filterBatchName, setFilterBatchName] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [isListing, setIsListing] = useState(false);
  const [listingMessage, setListingMessage] = useState("");
  const [listingError, setListingError] = useState("");

  // Auto-hide listing status after a short delay
  useEffect(() => {
    if (!listingMessage && !listingError) return;
    const timer = setTimeout(() => {
      setListingMessage("");
      setListingError("");
    }, 4000);
    return () => clearTimeout(timer);
  }, [listingMessage, listingError]);

  const ensureHttps = (url) => {
    if (!url) return null;
    if (url.startsWith("http://") && !url.includes("localhost")) {
      return url.replace("http://", "https://");
    }
    return url;
  };

  const DEFAULT_BASE_URL =
    "https://stickerchecker-new-722491567910.europe-west3.run.app";

  const API_BASE_URL = ensureHttps(
    import.meta.env.VITE_BASE_URL_DEVELOPMENT || DEFAULT_BASE_URL
  );

  const refreshAccessToken = async () => {
    const refreshToken = localStorage.getItem("refresh");
    if (!refreshToken) {
      // Show toast message and logout when no refresh token is found
      toastify("error", "No refresh access found. Please login again.");
      dispatch(signOutRequest({ func: () => {} }));
      throw new Error("No refresh token found. Please login again.");
    }

    const refreshResponse = await fetch(
      `${API_BASE_URL}/api/v1/token/refresh/`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh: refreshToken }),
      }
    );

    if (!refreshResponse.ok) {
      localStorage.removeItem("access");
      localStorage.removeItem("refresh");
      // Show toast message and logout when refresh token fails
      toastify("error", "No refresh access found. Please login again.");
      dispatch(signOutRequest({ func: () => {} }));
      throw new Error("Failed to refresh token. Please login again.");
    }

    const refreshData = await refreshResponse.json();
    localStorage.setItem("access", refreshData.access);
    return refreshData.access;
  };

  const normalizePageUrl = (url) => {
    if (!url) return null;
    try {
      const parsed = new URL(url);
      return `${API_BASE_URL}${parsed.pathname}${parsed.search}`;
    } catch {
      return url;
    }
  };

  const extractPageNumber = (url) => {
    if (!url) return null;
    const params = new URL(url).searchParams;
    return parseInt(params.get("page") || "1", 10);
  };

  const buildBaseListUrl = () => {
    const params = new URLSearchParams();
    params.set("page_size", String(12));
    if (ordering && ordering.length > 0) params.set("ordering", ordering);
    if (search && search.trim().length > 0) params.set("search", search.trim());
    if (filterId && String(filterId).trim().length > 0)
      params.set("id", String(filterId).trim());
    if (filterStatus && filterStatus.length > 0)
      params.set("ocr_status", filterStatus);
    if (filterBatch && filterBatch.trim().length > 0)
      params.set("batch__id", filterBatch.trim());
    if (filterBatchName && filterBatchName.trim().length > 0)
      params.set("batch__name", filterBatchName.trim());
    return `${API_BASE_URL}/api/v1/cards/?${params.toString()}`;
  };

  const fetchCards = useCallback(
    async (url = null, retry = false) => {
      try {
        setIsLoading(true);
        setError("");
        // Clear any old listing status on new fetch
        setListingMessage("");
        setListingError("");

        let token = localStorage.getItem("access");
        if (!token) token = await refreshAccessToken();

        const requestUrl = url ?? buildBaseListUrl();

        let response = await fetch(requestUrl, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (response.status === 401 && !retry) {
          const newToken = await refreshAccessToken();
          response = await fetch(requestUrl, {
            headers: {
              Authorization: `Bearer ${newToken}`,
              "Content-Type": "application/json",
            },
          });
        }

        if (!response.ok)
          throw new Error(`Failed to fetch cards: ${response.status}`);

        const data = await response.json();
        const results = Array.isArray(data.results) ? data.results : [];
        setCards(results);
        // Refresh any previously selected cards with up-to-date data
        setSelectedById((prev) => {
          if (!prev || prev.size === 0) return prev;
          const next = new Map(prev);
          for (const card of results) {
            if (next.has(card.id)) next.set(card.id, card);
          }
          return next;
        });
        const normalizedNext = normalizePageUrl(data.next);
        const normalizedPrev = normalizePageUrl(data.previous);
        setNextPage(normalizedNext);
        setPrevPage(normalizedPrev);

        const pageFromNext = extractPageNumber(normalizedNext);
        const pageFromPrev = extractPageNumber(normalizePageUrl(data.previous));
        if (!pageFromNext && !pageFromPrev) {
          setCurrentPage(1);
        } else if (pageFromPrev) {
          setCurrentPage(pageFromPrev + 1);
        } else if (pageFromNext) {
          setCurrentPage(pageFromNext - 1);
        }
      } catch (err) {
        setError(err?.message || "Something went wrong fetching cards");
      } finally {
        setIsLoading(false);
      }
    },
    [
      API_BASE_URL,
      ordering,
      search,
      filterId,
      filterStatus,
      filterBatch,
      filterBatchName,
    ]
  );

  useEffect(() => {
    fetchCards();
  }, [fetchCards]);

  const goToNext = () => {
    if (nextPage) fetchCards(nextPage);
  };

  const goToPrev = () => {
    if (prevPage) fetchCards(prevPage);
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
        setSelectedById((prevMap) => {
          const updated = new Map(prevMap);
          updated.delete(id);
          return updated;
        });
      } else {
        next.add(id);
        const card = cards.find((c) => c.id === id);
        if (card) {
          setSelectedById((prevMap) => {
            const updated = new Map(prevMap);
            updated.set(id, card);
            return updated;
          });
        }
      }
      return next;
    });
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
    setSelectedById(new Map());
  };

  const visibleCards = showSelectedOnly
    ? Array.from(selectedIds)
        .map((id) => selectedById.get(id))
        .filter(Boolean)
    : cards;

  const listSelectedOnEbay = async () => {
    if (selectedIds.size === 0 || isListing) return;
    try {
      setIsListing(true);
      setListingMessage("");
      setListingError("");

      const data = await startEbaySync(
        { card_ids: Array.from(selectedIds) },
        API_BASE_URL
      );

      const serverMsg =
        data?.message ||
        `Started eBay sync for ${selectedIds.size} card(s).`;
      setListingMessage(serverMsg);

      if (data?.sync_run_id) {
        navigate(EBAY_SYNC_DETAIL.replace(":syncRunId", data.sync_run_id));
      }
    } catch (err) {
      setListingError(err?.message || "Failed to list on eBay");
    } finally {
      setIsListing(false);
    }
  };

  return (
    <div className="ebay">
      {error && <div className="ebay__error">{error}</div>}
      {isLoading && <div className="ebay__loading">Loading...</div>}

      <div className="ebay__controls">
        <form
          className="ebay__search"
          onSubmit={(e) => {
            e.preventDefault();
            fetchCards(null, false);
          }}
        >
          <input
            type="text"
            placeholder="Search OCR text or metadata..."
            className="search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button type="submit" className="search-button">
            Search
          </button>
        </form>
        <div className="ebay__sort">
          <label htmlFor="ebay-ordering" className="sort-label">
            Sort by:
          </label>
          <select
            id="ebay-ordering"
            className="sort-select"
            value={ordering}
            onChange={(e) => {
              setOrdering(e.target.value);
              fetchCards(null, false);
            }}
          >
            <option value="-uploaded_at">Newest uploaded</option>
            <option value="uploaded_at">Oldest uploaded</option>
            <option value="-id">ID desc</option>
            <option value="id">ID asc</option>
          </select>
        </div>
        <button
          type="button"
          className={`refresh-button ${isLoading ? "disabled" : ""}`}
          onClick={() => fetchCards(null, false)}
          disabled={isLoading}
          title="Refresh"
        >
          Refresh
        </button>
        <button
          type="button"
          className="filters-toggle-button"
          onClick={() => setShowFilters((prev) => !prev)}
          aria-expanded={showFilters}
          aria-controls="ebay-filters"
        >
          Filters
          {[filterId, filterStatus, filterBatch, filterBatchName].filter(
            (v) => v && String(v).trim().length > 0
          ).length > 0 && (
            <span className="filters-badge">
              {
                [filterId, filterStatus, filterBatch, filterBatchName].filter(
                  (v) => v && String(v).trim().length > 0
                ).length
              }
            </span>
          )}
        </button>
      </div>

      {showFilters && (
        <form
          id="ebay-filters"
          className="ebay__filters"
          onSubmit={(e) => {
            e.preventDefault();
            fetchCards(null, false);
          }}
        >
          <div className="filter-group">
            <label htmlFor="ebay-filter-id" className="filter-label">
              Card ID
            </label>
            <input
              id="ebay-filter-id"
              type="number"
              className="filter-input"
              placeholder="e.g. 123"
              value={filterId}
              onChange={(e) => setFilterId(e.target.value)}
              min="1"
            />
          </div>
          <div className="filter-group">
            <label htmlFor="ebay-filter-status" className="filter-label">
              OCR Status
            </label>
            <select
              id="ebay-filter-status"
              className="filter-select"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="">Any</option>
              <option value="pending">Pending</option>
              <option value="processing">Processing</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
            </select>
          </div>
          <div className="filter-group">
            <label htmlFor="ebay-filter-batch" className="filter-label">
              Batch ID
            </label>
            <input
              id="ebay-filter-batch"
              type="number"
              className="filter-input"
              placeholder="e.g. 42"
              value={filterBatch}
              onChange={(e) => setFilterBatch(e.target.value)}
              min="1"
            />
          </div>
          <div className="filter-group">
            <label htmlFor="ebay-filter-batch-name" className="filter-label">
              Batch Name
            </label>
            <input
              id="ebay-filter-batch-name"
              type="text"
              className="filter-input"
              placeholder="e.g. July Batch"
              value={filterBatchName}
              onChange={(e) => setFilterBatchName(e.target.value)}
            />
          </div>
          <div className="filter-actions">
            <button type="submit" className="apply-button">
              Apply
            </button>
            <button
              type="button"
              className="clear-button"
              onClick={() => {
                setFilterId("");
                setFilterStatus("");
                setFilterBatch("");
                setFilterBatchName("");
                fetchCards(null, false);
              }}
            >
              Clear
            </button>
          </div>
        </form>
      )}
      <div className="ebay__toolbar">
        <span className="ebay__sel-count">{selectedIds.size} selected</span>
        <button
          className="ebay__toolbar-btn"
          onClick={() => setShowSelectedOnly((v) => !v)}
        >
          {showSelectedOnly ? "Show all" : "Show selected"}
        </button>
        {selectedIds.size > 0 && (
          <button className="ebay__toolbar-btn" onClick={clearSelection}>
            Clear
          </button>
        )}
        <span className="ebay__toolbar-spacer" />
        <button
          className={`ebay__primary-btn ${isListing ? "disabled" : ""}`}
          disabled={selectedIds.size === 0 || isListing}
          onClick={listSelectedOnEbay}
          title={
            selectedIds.size === 0
              ? "Select items to list"
              : "List selected on eBay"
          }
        >
          {isListing ? "Listing..." : "List on eBay"}
        </button>
      </div>

      {(listingMessage || listingError) && (
        <div className={`ebay__status ${listingError ? "error" : ""}`}>
          {listingError || listingMessage}
        </div>
      )}

      <div className="ebay__grid">
        {visibleCards.map((card) => (
          <div
            key={card.id}
            className={`ebay__item ${
              selectedIds.has(card.id) ? "selected" : ""
            }`}
            onClick={() => toggleSelect(card.id)}
            title="Click to select"
          >
            <img
              src={card.front_image}
              alt={`Card ${card.id}`}
              className="ebay__img"
              loading="lazy"
            />
            <div className="ebay__name">
              {card.metadata?.detailed_metadata?.["Card Name"] ??
                card.metadata?.title ??
                `Card #${card.id}`}
            </div>
          </div>
        ))}
      </div>

      {!showSelectedOnly && (
        <div className="ebay__pagination">
          <button
            className={`ebay__pagination-btn ${!prevPage ? "disabled" : ""}`}
            onClick={goToPrev}
            disabled={!prevPage || isLoading}
          >
            Previous
          </button>
          <span className="ebay__page-number">Page {currentPage}</span>
          <button
            className={`ebay__pagination-btn ${!nextPage ? "disabled" : ""}`}
            onClick={goToNext}
            disabled={!nextPage || isLoading}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

export default Ebay;
